import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api/client'
import type { LoanDetailView, LoanTypeView, LoanView } from '../types'

const BASE = '/v1/loans'

export function useLoanTypes() {
  return useQuery({
    queryKey: ['loans', 'types'],
    queryFn: async () => (await apiClient.get<LoanTypeView[]>(`${BASE}/types`)).data,
  })
}

export function useLoans() {
  return useQuery({
    queryKey: ['loans', 'list'],
    queryFn: async () => (await apiClient.get<LoanView[]>(BASE)).data,
  })
}

export function useLoanDetail(id: string | undefined) {
  return useQuery({
    queryKey: ['loans', 'detail', id],
    enabled: Boolean(id),
    queryFn: async () => (await apiClient.get<LoanDetailView>(`${BASE}/${id}`)).data,
  })
}

export function useLoanMutations() {
  const queryClient = useQueryClient()
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['loans'] })

  return {
    saveType: useMutation({
      mutationFn: async ({ id, ...body }: { id?: string } & Record<string, unknown>) =>
        id
          ? (await apiClient.put<LoanTypeView>(`${BASE}/types/${id}`, body)).data
          : (await apiClient.post<LoanTypeView>(`${BASE}/types`, body)).data,
      onSuccess: invalidate,
    }),
    /**
     * Retires a type.
     *
     * <p>Never a delete: an instalment schedule names the type it was written under, and
     * somebody querying a deduction two years from now has to be able to look it up.
     */
    retireType: useMutation({
      mutationFn: async (id: string) => {
        await apiClient.post(`${BASE}/types/${id}/deactivate`)
      },
      onSuccess: invalidate,
    }),
    apply: useMutation({
      mutationFn: async (body: Record<string, unknown>) =>
        (await apiClient.post<LoanView>(BASE, body)).data,
      onSuccess: invalidate,
    }),
    submit: useMutation({
      mutationFn: async (id: string) =>
        (await apiClient.post<LoanView>(`${BASE}/${id}/submit`)).data,
      onSuccess: invalidate,
    }),
    approve: useMutation({
      mutationFn: async ({ id, comment }: { id: string; comment?: string }) =>
        (await apiClient.post<LoanView>(`${BASE}/${id}/approve`, { comment })).data,
      onSuccess: invalidate,
    }),
    reject: useMutation({
      mutationFn: async ({ id, comment }: { id: string; comment?: string }) =>
        (await apiClient.post<LoanView>(`${BASE}/${id}/reject`, { comment })).data,
      onSuccess: invalidate,
    }),
    disburse: useMutation({
      mutationFn: async ({ id, disbursedOn }: { id: string; disbursedOn: string }) =>
        (await apiClient.post<LoanView>(`${BASE}/${id}/disburse`, { disbursedOn })).data,
      onSuccess: invalidate,
    }),
    settle: useMutation({
      mutationFn: async ({ id, amount, settledOn, notes }: {
        id: string; amount: number; settledOn: string; notes?: string
      }) => (await apiClient.post<LoanView>(
        `${BASE}/${id}/settle`, { amount, settledOn, notes })).data,
      onSuccess: invalidate,
    }),
  }
}
